import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '@/lib/api/client';
import { useToast } from '@/lib/hooks/useToast';
import { jobPath } from '@/lib/utils/navPaths';
import type { DailyLogJobStatus } from '@/lib/api/dashboard';

interface QuickDailyLogProps {
  jobs: DailyLogJobStatus[];
  onLogged: () => void;
}

// Quick daily-log entry straight from the dashboard (CR #88): pick a job,
// say what happened, done. Everything else uses the daily-log defaults.
export const QuickDailyLog = ({ jobs, onLogged }: QuickDailyLogProps) => {
  const { toast } = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [jobId, setJobId] = useState('');
  const [logDate, setLogDate] = useState(today);
  const [workPerformed, setWorkPerformed] = useState('');
  const [hours, setHours] = useState('8');
  const [crewSize, setCrewSize] = useState('1');
  const [saving, setSaving] = useState(false);
  // #94 — full job-page detail available right on the dashboard
  const [detailed, setDetailed] = useState(false);
  const [weather, setWeather] = useState('Clear');
  const [temperature, setTemperature] = useState('15');
  const [materials, setMaterials] = useState('');
  const [equipment, setEquipment] = useState('');
  const [visitors, setVisitors] = useState('');
  const [safety, setSafety] = useState('');
  const [notes, setNotes] = useState('');

  const WEATHER = ['Clear', 'Partly Cloudy', 'Cloudy', 'Rain', 'Heavy Rain', 'Snow', 'Fog', 'Windy'];

  const pending = useMemo(() => jobs.filter(j => !j.logged_today), [jobs]);
  const selected = jobs.find(j => String(j.id) === jobId);

  // CR #91 — last 14 calendar days (local), oldest first, for the streak grid
  const gridDays = useMemo(() => {
    const out: { date: string; dow: number; dayNum: number }[] = [];
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      out.push({ date: iso, dow: d.getDay(), dayNum: d.getDate() });
    }
    return out;
  }, []);

  // One tap on a gap cell prefills the form below with that job + date (#91)
  const backfill = (job: DailyLogJobStatus, date: string) => {
    setJobId(String(job.id));
    setLogDate(date);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jobId) { toast.error('Pick a job'); return; }
    if (!workPerformed.trim()) { toast.error('Describe the work performed'); return; }
    setSaving(true);
    try {
      await apiClient.post(`/jobs/${jobId}/daily-logs`, {
        log_date: logDate,
        work_performed: workPerformed.trim(),
        hours_worked: parseFloat(hours) || 8,
        crew_size: parseInt(crewSize, 10) || 1,
        ...(detailed ? {
          weather,
          temperature: parseInt(temperature, 10),
          materials_used: materials.trim() || undefined,
          equipment_used: equipment.trim() || undefined,
          visitors: visitors.trim() || undefined,
          safety_incidents: safety.trim() || undefined,
          notes: notes.trim() || undefined,
        } : {}),
      });
      toast.success('Daily log saved');
      setWorkPerformed('');
      setMaterials(''); setEquipment(''); setVisitors(''); setSafety(''); setNotes('');
      setJobId('');
      onLogged();
    } catch {
      toast.error('Failed to save daily log');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-bold text-gray-900 flex items-center gap-1.5">
          <span className="material-symbols-outlined text-lg text-primary">edit_note</span>
          Daily Log
        </h3>
        {jobs.length > 0 && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            pending.length === 0 ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
          }`}>
            {pending.length === 0 ? 'All logged today' : `${pending.length} job${pending.length === 1 ? '' : 's'} unlogged`}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">Quickly log today's work — weather and details can be filled in later on the job.</p>

      {/* CR #91 — 14-day streak & gaps grid: green = logged, red outline = gap
          on a working weekday (tap to backfill), gray = weekend / before start. */}
      {jobs.length > 0 && (
        <div className="mb-3 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-gray-500">Last 14 days</span>
            <span className="flex items-center gap-2 text-[10px] text-gray-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-[2px] bg-success inline-block" />logged</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-[2px] border border-critical/50 bg-critical/10 inline-block" />gap</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-[2px] bg-gray-200 inline-block" />off</span>
            </span>
          </div>
          <div className="space-y-1 overflow-x-auto">
            <div className="flex items-center gap-1">
              <span className="w-16 shrink-0" />
              {gridDays.map(d => (
                <span key={d.date} className="w-4 shrink-0 text-center text-[8px] leading-none text-gray-400">{d.dayNum}</span>
              ))}
            </div>
            {jobs.map(j => (
              <div key={j.id} className="flex items-center gap-1">
                <span className="w-16 shrink-0 text-[10px] font-medium text-gray-500 truncate" title={`${j.job_number} · ${j.title}`}>
                  {j.job_number}
                </span>
                {gridDays.map(d => {
                  const logged = j.log_dates.includes(d.date);
                  if (logged) {
                    return <span key={d.date} title={`${j.job_number} — logged ${d.date}`}
                      className="w-4 h-4 shrink-0 rounded-[3px] bg-success" />;
                  }
                  const beforeStart = !!j.start_date && d.date < j.start_date;
                  const weekend = d.dow === 0 || d.dow === 6;
                  if (weekend || beforeStart) {
                    return <span key={d.date} title={`${j.job_number} — ${beforeStart ? 'before job start' : 'weekend'}`}
                      className="w-4 h-4 shrink-0 rounded-[3px] bg-gray-200/70" />;
                  }
                  return (
                    <button key={d.date} type="button" onClick={() => backfill(j, d.date)}
                      title={`${j.job_number} — no log ${d.date}. Tap to backfill.`}
                      className="w-4 h-4 shrink-0 rounded-[3px] border border-critical/50 bg-critical/10 hover:bg-critical/30 transition-colors" />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-2.5">
        <select
          value={jobId}
          onChange={e => setJobId(e.target.value)}
          className="w-full h-9 px-2 rounded-lg border border-gray-300 text-sm"
        >
          <option value="">Select a job…</option>
          {jobs.map(j => (
            <option key={j.id} value={j.id}>
              {j.logged_today ? '✓ ' : ''}{j.job_number} · {j.title}
            </option>
          ))}
        </select>
        {selected?.logged_today && (
          <p className="text-[11px] text-gray-400">This job already has a log for today — saving adds another entry.</p>
        )}
        <textarea
          value={workPerformed}
          onChange={e => setWorkPerformed(e.target.value)}
          placeholder="Work performed today…"
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
        />
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 flex-1">
            <span className="text-[11px] font-medium text-gray-500">Date</span>
            <input type="date" value={logDate} max={today} onChange={e => setLogDate(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-300 text-sm w-full" />
          </label>
          <label className="flex flex-col gap-1 w-16">
            <span className="text-[11px] font-medium text-gray-500">Hours</span>
            <input type="number" step="0.5" min="0" value={hours} onChange={e => setHours(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-300 text-sm w-full" />
          </label>
          <label className="flex flex-col gap-1 w-14">
            <span className="text-[11px] font-medium text-gray-500">Crew</span>
            <input type="number" min="1" value={crewSize} onChange={e => setCrewSize(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-300 text-sm w-full" />
          </label>
          <button
            type="submit"
            disabled={saving}
            className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 whitespace-nowrap"
          >
            {saving ? 'Saving…' : 'Log it'}
          </button>
        </div>

        {/* Full job-page detail, folded in (#94) */}
        <button type="button" onClick={() => setDetailed(d => !d)}
          className="flex items-center gap-1 text-xs text-primary font-medium hover:underline">
          <span className={`material-symbols-outlined text-sm transition-transform ${detailed ? 'rotate-180' : ''}`}>expand_more</span>
          {detailed ? 'Less detail' : 'More detail — weather, materials, safety…'}
        </button>
        {detailed && (
          <div className="space-y-2 pt-1">
            <div className="flex gap-2">
              <label className="flex flex-col gap-1 flex-1">
                <span className="text-[11px] font-medium text-gray-500">Weather</span>
                <select value={weather} onChange={e => setWeather(e.target.value)}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm w-full">
                  {WEATHER.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 w-20">
                <span className="text-[11px] font-medium text-gray-500">Temp °C</span>
                <input type="number" value={temperature} onChange={e => setTemperature(e.target.value)}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm w-full" />
              </label>
            </div>
            <input value={materials} onChange={e => setMaterials(e.target.value)} placeholder="Materials used (optional)"
              className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm" />
            <input value={equipment} onChange={e => setEquipment(e.target.value)} placeholder="Equipment used (optional)"
              className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm" />
            <input value={visitors} onChange={e => setVisitors(e.target.value)} placeholder="Visitors / inspections (optional)"
              className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm" />
            <input value={safety} onChange={e => setSafety(e.target.value)} placeholder="Safety incidents (optional — leave blank if none)"
              className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm" />
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
        )}
      </form>
      {selected && (
        <Link
          to={`${jobPath(selected.client_id ?? undefined, selected.id)}/logs`}
          className="mt-2 inline-block text-xs text-primary font-medium hover:underline"
        >
          View this job's daily logs →
        </Link>
      )}
    </div>
  );
};
